import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory from 'react-bootstrap-table2-editor';
import filterFactory from 'react-bootstrap-table2-filter';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import { withClsCompTracking } from 'Telemetry/telemetry.service';
import 'App.css';
import CsvButton from 'Common/Buttons/CsvButton';
import ActionButton from 'Common/Buttons/ActionButton';
import ConfirmButton from 'Common/Buttons/ActionConfirmButton';
import Pagination from 'Common/Pagnination/Pagination';
import AppointeesColumns, { keyField } from './AppointeesColumns';
import { debounce, addKeyField } from 'utils';
import { getAppointees, updateAppointees } from '../../../Views/PostEx6/service';
import {
	onStartEdit,
	unhideRows,
	showRows,
	areRowsHidden,
	AmendmentInfo,
	UnhideButton,
	getEditState,
	setUnhideStatus,
	getUpdateRowEdit
} from '../../../../../ContractManagement/Markers/Datatables/Helpers/RowEditHelpers';

const { SearchBar, ClearSearchButton } = Search;

class AppointeesTable extends Component {
	constructor(props) {
		super(props);
		this.state = {
			keyField,
			appointees: [],
			origAppointees: [],
			updates: [],
			pageNo: 1,
			pageSize: 10,
			total: 0,
			isLoaded: false,
			...getEditState({ searchRef: 'searchComponentRef' })
		};
		this.componentRef = React.createRef();
		this.searchComponentRef = React.createRef();
		this.filter = filterFactory();
		this.onTableChange = debounce(this.onTableChange, 500);

		this.onStartEdit = onStartEdit.bind(this);
		this.unhideRows = unhideRows.bind(this);
		this.areRowsHidden = areRowsHidden.bind(this);
		this.getUpdateRowEdit = getUpdateRowEdit.bind(this);
		this.showRows = showRows.bind(this);
		this.setUnhideStatus = setUnhideStatus.bind(this);

		this.columns = AppointeesColumns(this.editStart());
	}

	async componentDidMount() {
		const { pageNo, pageSize } = this.state;
		const query = {
			sortOrder: 'asc',
			sortField: 'title',
			page: pageNo,
			sizePerPage: pageSize,
			searchText: '',
			flagExportData: false
		};
		const { total, appointees, origAppointees } = await this.getData(query);
		this.setState({ total, appointees, origAppointees, isLoaded: true });
	}

	componentDidUpdate() {
		this.setUnhideStatus();
	}

	getData = async query => {
		const { year, apptType, teamId, teamTitle, teamCode, handleErrors } = this.props;
		const { flagExportData: isExport } = query;
		const {
			status,
			data: { data, errors }
		} = await getAppointees(year, apptType, teamId, query);
		let { total, appointees } = data;

		if (status === 200) {
			appointees = isExport ? appointees : addKeyField(appointees, 'markerCode', 'name', 'code_name');
			appointees = appointees.map(appointee => {
				appointee.releaseDate = appointee.releaseDate === '0001-01-01T00:00:00' ? null : appointee.releaseDate;
				return {
					year,
					apptType,
					teamTitle,
					teamCode,
					teamId,
					releaseDate: null,
					processingIssue: !appointee.swap && !appointee?.releaseDate ? 'issue' : '',
					...appointee
				};
			});
			const origAppointees = cloneDeep(appointees);
			return { total, appointees, origAppointees };
		}
		handleErrors(['Error fetching Post Ex6 Appointees data', ...errors]);
		return { total: 0, appointees: [] };
	};

	onTableChange = async (type, state) => {
		let { sortOrder, sortField, page, sizePerPage, searchText } = state;
		sortOrder = sortOrder ? sortOrder : 'asc';
		sortField = sortField ? sortField : 'title';
		page = type === 'search' ? 1 : page;
		const query = { sortOrder, sortField, page, sizePerPage, searchText, flagExportData: false };
		const { total, appointees, origAppointees } = await this.getData(query);

		const updateRowEdit = this.getUpdateRowEdit(searchText);
		this.setState({ total, appointees, origAppointees, pageNo: page, pageSize: sizePerPage, ...updateRowEdit });
		this.componentRef.scrollIntoView({ behavior: 'smooth' });
	};

	exportData = async props => {
		const query = {
			sortOrder: 'asc',
			sortField: 'title',
			page: 0,
			sizePerPage: 0,
			searchText: '',
			flagExportData: true
		};
		const { appointees } = await this.getData(query);
		props.onExport(appointees);
	};

	nonEditableRows = () => {
		const { appointees } = this.state;
		return appointees.map(appointee => (!appointee.swap ? appointee[keyField] : false)).filter(item => item);
	};

	editStart = () => (row, column, rowIndex, columnIndex) => {
		const { appointees } = this.state;
		this.onStartEdit(row, column, rowIndex, columnIndex, appointees);
	};

	addUpdated = (fieldName, newValue, row) => {
		const { updates, origAppointees } = this.state;
		const updatedAppointee = updates.find(appointee => appointee[keyField] === row[keyField]);
		if (updatedAppointee) {
			updatedAppointee[fieldName] = newValue;
		} else {
			const originalAppointee = origAppointees.find(appointee => appointee[keyField] === row[keyField]);
			if (newValue !== originalAppointee[fieldName]) {
				updates.push(row);
				this.setState({ updates });
			}
		}
	};

	onUpdateClick = async () => {
		const { updates } = this.state;
		const updateList = updates.map(update => {
			const { appointmentId, releaseContract, releaseDate, year, apptType } = update;
			return {
				appointmentId,
				releaseContract,
				releaseDate,
				year,
				apptType
			};
		});

		const count = updateList.length;
		const { status, success, errors } = await updateAppointees(updateList);
		const { setNotice } = this.props;
		const appointeeText = count > 1 ? `${count} appointees` : `${count} appointee`;
		if (status === 200 && success) {
			this.setState({ updates: [] });
			setNotice({ message: `Successfully updated ${appointeeText}`, errors: [] });
		} else {
			setNotice({ message: `Unable to save ${appointeeText}`, errors });
		}

		this.showRows();
	};

	onBackClick = () => {
		const { setTeam, initialTeam } = this.props;
		setTeam(initialTeam);
	};

	render() {
		const { appointees, pageNo, pageSize, total, isLoaded, updates, hiddenRows } = this.state;
		const numberOfUpdates = updates.length;
		const hasUpdates = updates.length > 0;
		const { className = '', teamTitle } = this.props;
		const { exportData, columns, onTableChange, filter, onUpdateClick, onBackClick, nonEditableRows, editStart } = this;
		const rowsHidden = this.areRowsHidden();

		return (
			<div ref={el => (this.componentRef = el)} className={className}>
				<PaginationProvider
					pagination={paginationFactory({
						custom: true,
						totalSize: total,
						page: pageNo,
						sizePerPage: pageSize,
						showTotal: true,
						withFirstAndLast: true,
						alwaysShowAllBtns: true
					})}
					keyField={keyField}
					columns={columns}
					data={appointees}
				>
					{({ paginationProps, paginationTableProps }) => (
						<ToolkitProvider
							keyField={keyField}
							columns={columns}
							data={appointees}
							search={{ placeholder: 'Search...' }}
							exportCSV={{
								fileName: `post_Ex6_Appointees_${teamTitle.replaceAll(' ', '_')}.csv`,
								separator: ','
							}}
						>
							{toolkitProps => {
								const { searchText } = toolkitProps.searchProps;
								return (
									<div className="container-fluid">
										<div className="paginator">
											<div>
												<SearchBar ref={el => (this.searchComponentRef = el)} {...toolkitProps.searchProps} />
												{searchText && <ClearSearchButton {...toolkitProps.searchProps} className="clear-search" />}
											</div>
											<Pagination total={total} paginationProps={paginationProps} rowsHidden={rowsHidden} />
										</div>
										<BootstrapTable
											classes={'table table-primary table-striped table-sm table-responsive-md'}
											{...toolkitProps.baseProps}
											{...paginationTableProps}
											remote={{ sort: true, pagination: true, search: true }}
											onTableChange={onTableChange}
											filter={filter}
											bootstrap4
											striped
											condensed
											hover
											size="sm"
											hiddenRows={hiddenRows}
											cellEdit={cellEditFactory({
												mode: 'click',
												blurToSave: true,
												timeToCloseMessage: 10000,
												nonEditableRows: nonEditableRows,
												onStartEdit: editStart(),
												afterSaveCell: (oldValue, newValue, row, column) => this.addUpdated(column.dataField, newValue, row)
											})}
											loading={() => !isLoaded}
											noDataIndication={() => <p>No data Available</p>}
										/>
										{!rowsHidden && (
											<div className="paginator top-margin">
												<CsvButton onClick={exportData} {...toolkitProps.csvProps} />
												<Pagination total={total} paginationProps={paginationProps} />
											</div>
										)}
									</div>
								);
							}}
						</ToolkitProvider>
					)}
				</PaginationProvider>

				<AmendmentInfo count={numberOfUpdates} />
				<div className="dual-action-button">
					{rowsHidden ? (
						<UnhideButton onClick={this.unhideRows} numberOfUpdates={numberOfUpdates} inButtonSet={true} className="unhide" />
					) : (
						<ConfirmButton onClick={onBackClick} text="Back" cautionOn={hasUpdates} />
					)}
					<ActionButton onClick={onUpdateClick} text="Update" isDisabled={!hasUpdates} />
				</div>
			</div>
		);
	}
}

AppointeesTable.propTypes = {
	year: PropTypes.number.isRequired,
	apptType: PropTypes.string.isRequired,
	teamTitle: PropTypes.string.isRequired,
	teamCode: PropTypes.string.isRequired,
	teamId: PropTypes.number.isRequired,
	handleErrors: PropTypes.func.isRequired,
	setTeam: PropTypes.func.isRequired,
	initialTeam: PropTypes.object.isRequired,
	className: PropTypes.string,
	setNotice: PropTypes.func.isRequired
};

export default withClsCompTracking('Post Ex6 Appointees Table')(AppointeesTable);
